import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Divider, LinearProgress, Avatar, Tabs, Tab} from '@mui/material';
import Layout from '../components/Layout';
import ExpandableTable from '../components/ExpandableTable';
import api from '../services/api';
import { projectService } from '../services/api';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';


function POExpandedDetail({ po }) {
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/purchase-orders/' + po.id)
      .then(r => { if (!cancelled) { setInvoices(r.data.invoices || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setInvoices([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, [po.id]);

  const budget = Number(po.budget_amount || 0);
  const invoiced = Number(po.invoiced_amount || 0);
  const paid = Number(po.paid_amount || 0);
  const remaining = budget - invoiced;
  const pct = budget > 0 ? Math.min((invoiced / budget) * 100, 100) : 0;
  const paidPct = budget > 0 ? Math.min((paid / budget) * 100, 100) : 0;

  return (
    <Box>
      {/* Financial summary */}
      <Box sx={{ display: 'flex', mb: 2, background: '#f5f6fa', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        {[
          { label: 'Contracted', value: budget, color: '#1a237e' },
          { label: 'Invoiced', value: invoiced, color: '#e65100' },
          { label: 'Paid', value: paid, color: '#2e7d32' },
          { label: remaining >= 0 ? 'Remaining' : 'Over budget', value: Math.abs(remaining), color: remaining >= 0 ? '#2e7d32' : '#c62828' },
        ].map((item, i, arr) => (
          <Box key={item.label} sx={{ flex: 1, textAlign: 'center', p: 1.5, borderRight: i < arr.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
            <Typography variant="caption" sx={{ color: '#666' }}>{item.label}</Typography>
            <Typography sx={{ fontWeight: 700, color: item.color, fontSize: 18 }}>
              ${item.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>Invoiced {pct.toFixed(0)}%</Typography>
          <Typography variant="caption" sx={{ color: '#2e7d32' }}>Paid {paidPct.toFixed(0)}%</Typography>
        </Box>
        <Box sx={{ position: 'relative', height: 8, bgcolor: '#f5f6fa', borderRadius: 4 }}>
          <Box sx={{ position: 'absolute', height: '100%', width: pct + '%', bgcolor: '#e65100', borderRadius: 4, opacity: 0.3 }} />
          <Box sx={{ position: 'absolute', height: '100%', width: paidPct + '%', bgcolor: '#2e7d32', borderRadius: 4 }} />
        </Box>
      </Box>

      {/* Invoice list */}
      <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
        Invoices {invoices && invoices.length > 0 ? '(' + invoices.length + ')' : ''}
      </Typography>
      {loading && <Typography variant="caption" sx={{ color: '#999' }}>Loading invoices...</Typography>}
      {!loading && invoices && invoices.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f5f6fa', borderRadius: 2 }}>
          <ReceiptIcon sx={{ fontSize: 32, color: '#e0e0e0', mb: 0.5 }} />
          <Typography variant="body2" sx={{ color: '#999' }}>No invoices yet</Typography>
        </Box>
      )}
      {!loading && invoices && invoices.length > 0 && (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', bgcolor: '#f5f6fa', px: 2, py: 1, gap: 1 }}>
            {['Invoice #', 'Amount', 'Date', 'Status'].map(h => (
              <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: '#666' }}>{h}</Typography>
            ))}
          </Box>
          {invoices.map((inv, idx) => (
            <Box key={inv.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', px: 2, py: 1.25, gap: 1, alignItems: 'center', bgcolor: idx % 2 === 0 ? 'white' : '#fafbfc', borderTop: '1px solid #f5f6fa' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e', fontSize: 12 }}>{inv.invoice_number}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>${Number(inv.amount).toLocaleString()}</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>
                {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : (inv.submitted_at ? new Date(inv.submitted_at).toLocaleDateString() : '')}
              </Typography>
              <Chip label={inv.status === 'submitted' ? 'Pending' : inv.status} size="small"
                sx={{ fontSize: 10, height: 20, textTransform: 'capitalize',
                  bgcolor: inv.status === 'paid' ? '#e8f5e9' : inv.status === 'approved' ? '#e3f2fd' : '#fff3e0',
                  color: inv.status === 'paid' ? '#2e7d32' : inv.status === 'approved' ? '#1565c0' : '#e65100' }} />
            </Box>
          ))}
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', px: 2, py: 1.25, gap: 1, bgcolor: '#e8eaf6', borderTop: '2px solid #c5cae9' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1a237e' }}>
              ${invoices.reduce((a, i) => a + Number(i.amount), 0).toLocaleString()}
            </Typography>
            <Box />
            <Typography variant="caption" sx={{ color: '#666' }}>
              {invoices.filter(i => i.status === 'paid').length} paid
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function PurchaseOrders() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project_id');
  const [pos, setPOs] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [viewDialog, setViewDialog] = useState({ open: false, po: null });
  const [poInvoices, setPoInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => { fetchPOs(); }, []);

  const fetchPOs = async () => {
    try {
      const res = await api.get('/purchase-orders' + (projectId ? `?project_id=${projectId}` : ''));
      setPOs(res.data);
    } catch (err) {}
  };

  const handleViewPO = async (po) => {
    setViewDialog({ open: true, po });
    setPoInvoices([]);
    setLoadingInvoices(true);
    try {
      const res = await api.get(`/purchase-orders/${po.id}`);
      setPoInvoices(res.data.invoices || []);
    } catch (err) {}
    setLoadingInvoices(false);
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const totalBudget = pos.reduce((s, p) => s + Number(p.budget_amount || 0), 0);
  const totalInvoiced = pos.reduce((s, p) => s + Number(p.invoiced_amount || 0), 0);
  const totalPaid = pos.reduce((s, p) => s + Number(p.paid_amount || 0), 0);

  return (
    <Layout>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Purchase Orders</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>Auto-generated from signed contracts</Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total POs', value: pos.length, bg: 'linear-gradient(135deg, #1a237e, #283593)' },
          { label: 'Total Contracted', value: `${totalBudget.toLocaleString()}`, bg: 'linear-gradient(135deg, #6a1b9a, #8e24aa)' },
          { label: 'Total Invoiced', value: `${totalInvoiced.toLocaleString()}`, sub: `${totalBudget > 0 ? ((totalInvoiced/totalBudget)*100).toFixed(0) : 0}% of contracted`, bg: 'linear-gradient(135deg, #e65100, #f57c00)' },
          { label: 'Total Paid', value: `${totalPaid.toLocaleString()}`, sub: `${totalBudget > 0 ? ((totalPaid/totalBudget)*100).toFixed(0) : 0}% of contracted`, bg: 'linear-gradient(135deg, #2e7d32, #43a047)' },
          { label: 'Remaining', value: `${(totalBudget - totalInvoiced).toLocaleString()}`, sub: `${(totalBudget - totalPaid).toLocaleString()} unpaid`, bg: (totalBudget - totalInvoiced) >= 0 ? 'linear-gradient(135deg, #00695c, #00897b)' : 'linear-gradient(135deg, #c62828, #e53935)' },
        ].map((card, i) => (
          <Grid item xs={6} md={2.4} key={i}>
            <Card elevation={0} sx={{ borderRadius: 3, background: card.bg, color: 'white' }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{card.value}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>{card.label}</Typography>
                {card.sub && <Typography variant="caption" sx={{ opacity: 0.7 }}>{card.sub}</Typography>}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* PO List */}
      {pos.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', p: 6, textAlign: 'center' }}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#999' }}>No Purchase Orders yet</Typography>
          <Typography variant="body2" sx={{ color: '#ccc' }}>POs are automatically created when contracts are fully signed</Typography>
        </Card>
      ) : (
        (() => {
          const isClosed = (po) => {
            const budget = Number(po.budget_amount || 0);
            const paid = Number(po.paid_amount || 0);
            return budget > 0 && paid >= budget;
          };
          const activePOs = pos.filter(p => !isClosed(p));
          const closedPOs = pos.filter(p => isClosed(p));
          const visible = (activeTab === 0 ? activePOs : closedPOs).slice().sort((a, b) => {
            const an = (a.vendor_name || ((a.first_name || '') + ' ' + (a.last_name || '')).trim()).toLowerCase();
            const bn = (b.vendor_name || ((b.first_name || '') + ' ' + (b.last_name || '')).trim()).toLowerCase();
            return an.localeCompare(bn);
          });
          return (
            <>
              <Tabs value={activeTab} onChange={(e,v)=>setActiveTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Tab label={`Active (${activePOs.length})`} />
                <Tab label={`Closed (${closedPOs.length})`} />
              </Tabs>
              {visible.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                  <Typography>{activeTab === 0 ? 'No active purchase orders — all POs are paid in full.' : 'No closed purchase orders yet.'}</Typography>
                </Box>
              ) : (
        <ExpandableTable
          rows={visible}
          emptyMessage="No purchase orders in this view."
          columns={[
            {
              key: 'vendor_name',
              label: 'Vendor',
              render: (po) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Avatar sx={{ bgcolor: '#1a237e', width: 32, height: 32, fontSize: 14 }}>
                    {(po.vendor_name || po.first_name || '?')[0]}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }} noWrap>
                      {po.vendor_name || ((po.first_name || '') + ' ' + (po.last_name || '')).trim() || '—'}
                    </Typography>
                    {po.trade_specialty && <Typography variant="caption" sx={{ color: '#888' }} noWrap>{po.trade_specialty}</Typography>}
                  </Box>
                </Box>
              )
            },
            {
              key: 'po_number',
              label: 'PO #',
              width: 150,
              render: (po) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12, color: '#1a237e' }}>
                    {po.po_number}
                  </Typography>
                  {po.title && <Typography variant="caption" sx={{ color: '#666' }} noWrap>{po.title}</Typography>}
                </Box>
              )
            },
            {
              key: 'budget_amount',
              label: 'Contracted',
              align: 'right',
              width: 110,
              render: (po) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e', fontVariantNumeric: 'tabular-nums' }}>
                  ${Number(po.budget_amount || 0).toLocaleString()}
                </Typography>
              )
            },
            {
              key: 'invoiced_amount',
              label: 'Invoiced',
              align: 'right',
              width: 110,
              render: (po) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100', fontVariantNumeric: 'tabular-nums' }}>
                  ${Number(po.invoiced_amount || 0).toLocaleString()}
                </Typography>
              )
            },
            {
              key: 'remaining',
              label: 'Remaining',
              align: 'right',
              width: 110,
              render: (po) => {
                const rem = Number(po.budget_amount || 0) - Number(po.invoiced_amount || 0);
                return (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: rem >= 0 ? '#2e7d32' : '#c62828', fontVariantNumeric: 'tabular-nums' }}>
                    ${Math.abs(rem).toLocaleString()}{rem < 0 ? ' over' : ''}
                  </Typography>
                );
              }
            },
            {
              key: 'status',
              label: 'Status',
              width: 110,
              render: (po) => {
                const budget = Number(po.budget_amount || 0);
                const paid = Number(po.paid_amount || 0);
                const closed = paid >= budget && budget > 0;
                return (
                  <Chip label={closed ? 'closed' : po.status} size="small"
                    sx={{ textTransform: 'capitalize', bgcolor: closed ? '#eceff1' : (po.status === 'active' ? '#e8f5e9' : '#f5f5f5'),
                      color: closed ? '#37474f' : (po.status === 'active' ? '#2e7d32' : '#666'), fontWeight: 600, height: 22, fontSize: 11 }} />
                );
              }
            }
          ]}
          renderExpanded={(po) => <POExpandedDetail po={po} />}
        />
              )}
            </>
          );
        })()
      )}

      {/* PO Detail Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, po: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewDialog.po && (() => {
          const po = viewDialog.po;
          const budget = Number(po.budget_amount || 0);
          const invoiced = Number(po.invoiced_amount || 0);
          const paid = Number(po.paid_amount || 0);
          return (
            <>
              <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }} noWrap title={po.vendor_name || ''}>{po.vendor_name || `${po.first_name || ''} ${po.last_name || ''}`.trim() || '—'}</Typography>
                  <Chip label={po.status} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{po.title}</Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{po.po_number}</Typography>

                <Box sx={{ bgcolor: '#f5f6fa', borderRadius: 2, p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Vendor</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {po.vendor_name || `${po.first_name || ''} ${po.last_name || ''}`.trim()}
                  </Typography>
                  {po.trade_specialty && <Typography variant="caption" sx={{ color: '#666' }}>{po.trade_specialty}</Typography>}
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    { label: 'Contracted', value: `$${budget.toLocaleString()}`, color: '#1a237e' },
                    { label: 'Invoiced', value: `$${invoiced.toLocaleString()}`, color: '#e65100' },
                    { label: 'Paid', value: `$${paid.toLocaleString()}`, color: '#2e7d32' },
                    { label: 'Remaining', value: `$${(budget - invoiced).toLocaleString()}`, color: budget - invoiced >= 0 ? '#2e7d32' : '#c62828' },
                  ].map((item, i) => (
                    <Grid item xs={6} key={i}>
                      <Typography variant="caption" sx={{ color: '#999' }}>{item.label}</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: item.color }}>{item.value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate"
                    value={budget > 0 ? Math.min((paid / budget) * 100, 100) : 0}
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {budget > 0 ? ((paid / budget) * 100).toFixed(0) : 0}% paid
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1.5 }}>
                  Invoices {poInvoices.length > 0 && `(${poInvoices.length})`}
                </Typography>
                {loadingInvoices ? (
                  <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>Loading invoices...</Typography>
                ) : poInvoices.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                    <ReceiptIcon sx={{ fontSize: 36, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#999' }}>No invoices yet</Typography>
                  </Box>
                ) : (
                  <Box sx={{ border: '1px solid #e8eaf6', borderRadius: 2, overflow: 'hidden' }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', bgcolor: '#f5f6fa',
                      px: 2, py: 1, gap: 1 }}>
                      {['Invoice #', 'Amount', 'Date', 'Status'].map(h => (
                        <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: '#666' }}>{h}</Typography>
                      ))}
                    </Box>
                    {poInvoices.map((inv, idx) => (
                      <Box key={inv.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        px: 2, py: 1.5, gap: 1, alignItems: 'center',
                        bgcolor: idx % 2 === 0 ? 'white' : '#fafafa',
                        borderTop: '1px solid #f5f6fa' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e', fontSize: 11 }}>
                          {inv.invoice_number}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          ${Number(inv.amount).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : 
                           new Date(inv.submitted_at).toLocaleDateString()}
                        </Typography>
                        <Chip label={inv.status === 'submitted' ? 'Pending' : inv.status} size="small"
                          sx={{ fontSize: 10, height: 20, textTransform: 'capitalize',
                            bgcolor: inv.status === 'paid' ? '#e8f5e9' : inv.status === 'approved' ? '#e3f2fd' : '#fff3e0',
                            color: inv.status === 'paid' ? '#2e7d32' : inv.status === 'approved' ? '#1565c0' : '#e65100' }} />
                      </Box>
                    ))}
                    {/* Totals row */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                      px: 2, py: 1.5, gap: 1, bgcolor: '#e8eaf6', borderTop: '2px solid #c5cae9' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#1a237e' }}>
                        ${poInvoices.reduce((s, i) => s + Number(i.amount), 0).toLocaleString()}
                      </Typography>
                      <Box />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {poInvoices.filter(i => i.status === 'paid').length} paid
                      </Typography>
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setViewDialog({ open: false, po: null })} sx={{ borderRadius: 2 }}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Layout>
  );
}
